import {
    Divider,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { PledgeTransactionTypeCode, pledgeTransactionTypeCodeToEnum } from '@tassat/common-definitions';
import { ActionModal } from '@tassat/modal';
import React from 'react';

import { formatEpochDate } from 'Lib/stringHelpers';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import Worker from 'Lib/worker/PrimusWorkerWrapper';
import { useAppSelector } from 'Reducers/store/hooks';
import { getPledgeAccountDetail } from 'Selectors/accounts';
import { getPledgeDetail, getUser } from 'Selectors/common';

import { PledgeContextWorkflowContext } from '../../pledgeContextWorkflow';

export const muiStyles = (theme: ApplicationMuiTheme) => ({
    amountText: {
        padding: '.5rem',
        paddingBottom: '1.5rem'
    },
    bodyContent: { padding: '0 3rem' },
    contentDivider: {
        marginTop: '1rem',
        marginBottom: '1.5rem'
    },
    disclaimerContainer: { padding: '1rem 0 1.5rem 0' },
    disclaimerText: { flexBasis: '33%' },
    disclaimerTextBold: { fontWeight: 'bold' },
    table: {
        borderCollapse: 'separate' as const,
        borderSpacing: '.5rem'
    },
    tableRow: { marginBottom: '.3rem' },
    tableLabelCell: {
        backgroundColor: theme?.app?.applicationColors.tableRowLabelColor || 'rgba(185, 217, 236, 5)',
        fontWeight: 'bold' as const
    },
    tableCell: {
        border: 'none' as const,
        paddingTop: '.3rem',
        paddingBottom: '.3rem'
    }
});

const useStyles = makeStyles(muiStyles);

const pledgeActionsMap: Partial<Record<string, string>> = {
    [PledgeTransactionTypeCode.pledge_add_on]: 'an add on',
    [PledgeTransactionTypeCode.reclaim]: 'a reclaim',
    [PledgeTransactionTypeCode.partial_reclaim]: 'a partial reclaim',
    [PledgeTransactionTypeCode.release]: 'a release',
    [PledgeTransactionTypeCode.partial_release]: 'a partial release'
};

function PledgeApproval() {
    const classes = useStyles();
    const { pledgeContextWorkflowState, dispatchPledgeContextWorkflowAction } =
        React.useContext(PledgeContextWorkflowContext)!;
    const { selectedPledgeTransaction, selectedPledge } = pledgeContextWorkflowState;

    const currentUser = useAppSelector(getUser);
    const pledgeDetail = useAppSelector(getPledgeDetail);
    const pledgeAccountDetail = useAppSelector((state) => getPledgeAccountDetail(state, pledgeDetail.PledgeId, pledgeDetail));

    const handleCancel = () => {
        dispatchPledgeContextWorkflowAction({
            type: 'CHANGE_MODAL_SCREEN',
            payload: { screen: 'details' }
        });
    };

    const handleConfirm = async () => {
        if (!selectedPledgeTransaction || !currentUser) return;
        try {
            const {
                PledgeTransactionRequestId,
                PledgeTransactionType
            } = selectedPledgeTransaction;
            const transactionTypeEnum = pledgeTransactionTypeCodeToEnum(PledgeTransactionType);
            if (!transactionTypeEnum) return;

            await Worker.getInstance()
                .approveTransaction(
                    PledgeTransactionRequestId,
                    currentUser,
                    PledgeTransactionType,
                    pledgeAccountDetail.AccountId
                );

            // TODO Add actions for each workflow
            dispatchPledgeContextWorkflowAction({
                type: 'SET_MODAL_PLEDGE_VALUES',
                payload: {
                    RequestId: selectedPledgeTransaction.PledgeTransactionRequestId,
                    AssetQuantity: selectedPledgeTransaction.PledgeTransactionAmount,
                    Ticker: selectedPledge?.Ticker,
                    ReceivingAccountName: pledgeDetail.PledgeParticipant[0]?.PledgeParticipantAccountName
                }
            });
            dispatchPledgeContextWorkflowAction({
                type: 'SET_MODAL_ACTION',
                payload: 'approve'
            });
            dispatchPledgeContextWorkflowAction({
                type: 'SET_PLEDGE_TYPE',
                payload: transactionTypeEnum
            });
            dispatchPledgeContextWorkflowAction({
                type: 'SET_PLEDGE_ACTION_IN_PROGRESS',
                payload: true
            });
            dispatchPledgeContextWorkflowAction({
                type: 'CHANGE_MODAL_SCREEN',
                payload: { screen: 'details' }
            });
        } catch (error) {
            console.error(error);
        }
    };

    if (!selectedPledgeTransaction || !pledgeDetail) return null;

    const flowLabel = pledgeActionsMap[selectedPledgeTransaction.PledgeTransactionType] ?? 'a transaction';
    const recipientAccountName = pledgeDetail.PledgeParticipant[0]?.PledgeParticipantAccountName ?? '';
    const postAmount = `${selectedPledge?.Ticker} ${pledgeDetail.AssetAmount}`;
    const transactionAmount = `${selectedPledge?.Ticker} ${selectedPledgeTransaction.PledgeTransactionAmount}`;

    return (
        <ActionModal
            cancelButton={{
                display: 'show',
                onCancel: handleCancel
            }}
            headerStyle='approve'
            onClose={handleCancel}
            open
            submitButton={{
                display: 'show',
                text: 'CONFIRM',
                onSubmit: handleConfirm
            }}
            title='APPROVE'
        >
            <Grid
                className={classes.bodyContent}
                container
                direction='column'
                spacing={0}
                wrap='nowrap'
            >
                <Grid item>
                    <Typography variant='body1'>
                        {`You are about to approve ${flowLabel} of`}
                    </Typography>
                    <Typography
                        className={classes.amountText}
                        variant='h3'
                    >
                        {transactionAmount}
                    </Typography>
                </Grid>
                <Grid
                    className={classes.contentDivider}
                    component={Divider}
                    item
                />
                <Grid
                    container
                    direction='row'
                    item
                    justifyContent='space-around'
                    spacing={0}
                >
                    <Grid
                        container
                        direction='column'
                        item
                        lg={6}
                        md={12}
                        spacing={0}
                        wrap='nowrap'
                    >
                        <Table className={classes.table}>
                            <TableHead />
                            <TableBody>
                                <TableRow className={classes.tableRow}>
                                    <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Account Name</TableCell>
                                    <TableCell className={classes.tableCell}>
                                        {selectedPledgeTransaction.PledgeTransactionRequesterAccountName}
                                    </TableCell>
                                </TableRow>
                                <TableRow className={classes.tableRow}>
                                    <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Recipient Account Name</TableCell>
                                    <TableCell className={classes.tableCell}>{recipientAccountName}</TableCell>
                                </TableRow>
                                <TableRow className={classes.tableRow}>
                                    <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Post Amount</TableCell>
                                    <TableCell className={classes.tableCell}>{postAmount}</TableCell>
                                </TableRow>
                                <TableRow className={classes.tableRow}>
                                    <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Public Description</TableCell>
                                    <TableCell className={classes.tableCell}>{pledgeDetail.PublicDescription}</TableCell>
                                </TableRow>
                                <TableRow className={classes.tableRow}>
                                    <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Private Description</TableCell>
                                    <TableCell className={classes.tableCell}>{pledgeDetail.PrivateDescription}</TableCell>
                                </TableRow>
                                <TableRow className={classes.tableRow}>
                                    <TableCell className={`${classes.tableCell} ${classes.tableLabelCell}`}>Timestamp</TableCell>
                                    <TableCell className={classes.tableCell}>
                                        {formatEpochDate(selectedPledgeTransaction.PledgeTransactionAuditTime)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Grid>
                    <Grid
                        className={classes.disclaimerContainer}
                        container
                        direction='column'
                        item
                        lg={4}
                        md={12}
                        spacing={0}
                        wrap='nowrap'
                    >
                        {
                            Number(selectedPledgeTransaction.PledgeTransactionType) === PledgeTransactionTypeCode.new_pledge ||
                            Number(selectedPledgeTransaction.PledgeTransactionType) === PledgeTransactionTypeCode.pledge_add_on ||
                            Number(selectedPledgeTransaction.PledgeTransactionType) === PledgeTransactionTypeCode.release ||
                            Number(selectedPledgeTransaction.PledgeTransactionType) === PledgeTransactionTypeCode.partial_release ?
                                <>
                                    <Typography
                                        className={classes.disclaimerText}
                                        variant='body1'
                                    >
                                        Please review the details of your post collateral request.
                                    </Typography>
                                    <Typography
                                        className={`${classes.disclaimerText} ${classes.disclaimerTextBold}`}
                                        variant='body1'
                                    >
                                        All transfers following a collateral post are final and you will not be able to reclaim
                                        your shares if such shares are transferred to the recipient&apos;s account.
                                    </Typography>
                                </>
                            : null
                        }
                    </Grid>
                </Grid>
                <Grid
                    className={classes.contentDivider}
                    component={Divider}
                    item
                />
            </Grid>
        </ActionModal>
    );
}
export { PledgeApproval as Approval };